import React, { useState, useEffect } from 'react';
import { EditOutlined } from "@ant-design/icons";
import { Button } from 'antd';
import ApplicationService from '../services/axios.service';
import { months } from '../util/common'; // Import the months array
import { FaDownload, FaUpload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Spinner from './spinner';

const ForecastingVsBudget = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [availableYears, setAvailableYears] = useState([]);
  const [halfyear, setHalfYear] = useState('');
  const [data, setData] = useState([]);
  const [captionData, setCaptionData] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const [loading, setLoading] = useState(false);
  const service = new ApplicationService();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = { year, month, halfyear };
      const response = await service.forecastandbudget(payload);
      const result = response.data;
      if (result.statusCode == '200' && result.data) {
        const formattedData = result.data.map((item) => ({
          id: item.id,
          model: item.model,
          options: item.options,
          date: item.date,
          budget: `${item.budget}`,
          prev_forecast: `${item.prev_forecast}`,
          forecast: `${item.forecast}`,
          diff_forecast_budget: `${item.diff_forecast_budget}`,
          diff_forecast_prev: `${item.diff_forecast_prev}`,
          action: item.action // 1 = editable, 0 = read-only
        }));
        setData(formattedData);
        setCaptionData(result.captiondata);
      } else {
        // If no records found or statusCode isn't 200, clear data
        setData([]);
        setCaptionData(result.captiondata || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
      setCaptionData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchYears = async () => {
    try {
      const response = await service.getforecastingYears();
      const result = response.data;
      if (result.statusCode == '200') {
        const years = result.data;
        setAvailableYears(years);        // Store all available years
        if (years.length > 0) {
          setYear(years[0]);             // Set the first year as default
        } else {
          setYear(new Date().getFullYear()); // Fallback to current year if no years available
        }
      } else {
        console.log('Error fetching years:', result.message);
      }
    } catch (error) {
      console.log('Error fetching years:', error);
    }
  };

  // Initial fetch (if needed)
  useEffect(() => {
    fetchData();
    fetchYears();
  }, []);

  const handleSearch = () => {
    fetchData();
  };

  const handleEdit = (row) => {
    if (row.action === 1) {
      setEditingRowId(row.id);
      setEditedRow({ ...row });
    }
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setEditedRow({});
  };

  const handleInputChange = (e, field) => {
    setEditedRow({ ...editedRow, [field]: e.target.value });
  };

  const handleSave = async () => {
    try {
      // Prepare payload by stripping '$' from numeric values.
      const updatedPayload = {
        id: editedRow.id,
        price: editedRow.budget.replace('$', ''),
        fc: editedRow.forecast.replace('$', ''),
        fc_date: editedRow.date,
      };

      const updateResponse = await service.updateForecastBudget(updatedPayload);
      const updateResult = updateResponse.data;
      if (updateResult.statusCode == '200') {
        // Update local state on successful API update.
        toast.success("Row updated successfully!");
        fetchData();
        setEditingRowId(null);
        setEditedRow({});
      }
    } catch (error) {
      console.error("Error updating row:", error);
      toast.error("Failed to update row.");
    }
  };

  const exportData = async () => {
    const payload = {
      year: year,
      month: month,
      halfyear: halfyear,
    }
    setLoading(true);
    try {
      const response = await service.exportForecastBudget(payload);
      const result = response.data;
      if (response.status === 200 && result) {
        const fileName = "forecastvsbudget_report.xlsx";  // Choose a name for the file

        // Create a Blob from the response
        const blob = new Blob([result], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;

        // Trigger the download
        link.click();
      } else {
        console.error("Error downloading data:", result.message);
      }
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (<>
    <div className="p-4 bg-white w-full mb-4 shadow-md rounded-md">
      {/* Filter Section */}
      <div className="p-4">
        <div className="flex gap-x-5 mb-4">
          <div className="w-1/4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Year</label>
            <select
              id="year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 h-10 cursor-pointer"
            >
              <option value="">Select Year</option>
              {availableYears.map((yr, idx) => (
                <option key={idx} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          </div>
          <div className="w-1/4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Month</label>
            <select
              id="month"
              value={month}
              // onChange={(e) => setMonth(e.target.value)}
              onChange={(e) => {
                setMonth(e.target.value);
                // Clear halfyear if a month is selected
                if (e.target.value) {
                  setHalfYear('');
                }
              }}
              className="w-full border border-gray-300 rounded px-2 py-1 h-10 cursor-pointer"
            >
              <option value="">Select Month</option>
              {months.map((m, index) => (
                <option key={index} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div className="w-1/4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Half Year</label>
            <select
              id="halfyear"
              value={halfyear}
              // onChange={(e) => setHalfYear(e.target.value)}
              onChange={(e) => {
                setHalfYear(e.target.value);
                // Clear month if a half year is selected
                if (e.target.value) {
                  setMonth('');
                }
              }}
              className="w-full border border-gray-300 rounded px-2 py-1 h-10 cursor-pointer"
            >
              <option value="">Select Half Year</option>
              <option value="A">A</option>
              <option value="B">B</option>
            </select>
          </div>
          <div className="w-1/4 flex items-end">
            <button
              onClick={handleSearch}
              className="bg-[#3D3D3D] text-white px-4 py-2 h-10 rounded-md w-full cursor-pointer"
            >
              <span className="text-white">Search</span>
            </button>
          </div>
        </div>
      </div>

      <div className="ml-auto flex gap-x-5 justify-end py-2"  >
        {/* Import Button with icon */}
        {/* <button className="bg-[#287DD1] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer" onClick={() => navigate("/upload-forecast-excel")}>
          <FaUpload className="text-white" /> 
          <span className="text-white">Import</span>
        </button> */}

        {/* Export Button with icon */}
        <button className="bg-[#107C41] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer" onClick={exportData}        >

          <FaDownload className="text-white" /> {/* Download icon */}
          <span className="text-white">Export</span>
        </button>
      </div>

      {/* Table Section */}
      {/* <div className=" overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Spinner />
          </div>
        ) : data.length > 0 ? (<>
          <table className="min-w-full table-auto border border-[#D5D5D5]">
            <thead className="bg-[#EAEEFF] sticky top-0 z-10">
              <tr>
                <th colSpan={captionData.length + 1} className="px-4 py-2 font-medium text-center">
                  {year} SALES  (Break by Models/Customers)
                </th>
              </tr>
              <tr className="bg-[#F8F9FF]">
                {captionData && captionData.length > 0 ? (
                  captionData.map((header, index) => (
                    <th key={index} className="px-4 py-2 font-medium text-left whitespace-nowrap ">
                      {header}
                    </th>
                  ))
                ) : (
                  data[0] &&
                  Object.keys(data[0])
                    .filter((key) => key !== 'id' && key !== 'action')
                    .map((key, index) => (
                      <th key={index} className="px-4 py-2 font-medium text-left whitespace-nowrap ">
                        {key}
                      </th>
                    ))
                )}
                <th className="px-4 py-2 font-medium text-left">Actions</th>
              </tr>
            </thead>
          </table>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="min-w-full table-auto border border-[#D5D5D5]">
              <tbody>
                {data.map((row) => (
                  <>
                    <tr key={row.id} className="bg-white hover:bg-gray-50 border-b border-[#D5D5D5]">
                      {Object.entries(row)
                        .filter(([key]) => key !== 'id' && key !== 'action')
                        .map(([key, value], index) => (
                          <td key={index} className="px-4 py-2 whitespace-nowrap ">
                            {editingRowId === row.id &&
                              row.action === 1 &&
                              (key === 'budget' || key === 'forecast') ? (

                              <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">$</span>
                                <input
                                  type="number"
                                  value={editedRow[key]}
                                  onChange={(e) => handleInputChange(e, key)}
                                  className="border  pl-5 border-gray-300 rounded px-2 py-1"
                                />
                              </div>
                            ) : (
                              value
                            )}
                          </td>
                        ))}
                      <td className="px-4 py-2 whitespace-nowrap ">
                        {row.action === 1 && (
                          editingRowId === row.id ? (
                            <div className="flex space-3">
                              <Button
                                onClick={() => handleSave(row.id)}
                                style={{ marginRight: 8 }}
                              >
                                Save
                              </Button>
                              <Button onClick={handleCancel}>Cancel</Button>
                            </div>
                          ) : (
                            <EditOutlined
                              onClick={() => handleEdit(row)}
                              style={{ marginRight: 8, cursor: 'pointer' }}
                            />
                          )
                        )}
                      </td>
                    </tr>


                  </>
                ))}
              </tbody>
            </table>
          </div>
        </>
        ) : (
          <div className="text-center py-10">No records found</div>
        )}
      </div> */}

      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Spinner />
          </div>
        ) : data.length > 0 ? (
          <div className="max-h-[500px] overflow-y-auto">
            <table className="min-w-full table-fixed border border-[#D5D5D5]">
              <thead className="bg-[#EAEEFF] sticky top-0 z-10">
                <tr>
                  <th colSpan={(captionData.length || Object.keys(data[0]).length - 2) + 1} className="px-4 py-2 font-medium text-center">
                    {year} SALES (Break by Models/Customers)
                  </th>
                </tr>
                <tr className="bg-[#F8F9FF]">
                  {(captionData && captionData.length > 0
                    ? captionData
                    : Object.keys(data[0]).filter((key) => key !== 'id' && key !== 'action')
                  ).map((header, index) => (
                    <th key={index} className="px-4 py-2 font-medium text-left whitespace-nowrap w-[150px]">
                      {header}
                    </th>
                  ))}
                  {/* <th className="px-4 py-2 font-medium text-left w-[100px]">Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.id} className="bg-white hover:bg-gray-50 border-b border-[#D5D5D5]">
                    {Object.entries(row)
                      .filter(([key]) => key !== 'id' && key !== 'action')
                      .map(([key, value], index) => (
                        <td key={index} className="px-4 py-2 whitespace-nowrap w-[150px]">
                          {editingRowId === row.id &&
                            row.action === 1 &&
                            (key === 'budget' || key === 'forecast') ? (
                            <div className="relative">
                              <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">$</span>
                              <input
                                type="number"
                                value={editedRow[key]}
                                onChange={(e) => handleInputChange(e, key)}
                                className="border pl-5 border-gray-300 rounded px-2 py-1 w-full"
                              />
                            </div>
                          ) : (
                            value
                          )}
                        </td>
                      ))}
                    {/* <td className="px-4 py-2 whitespace-nowrap w-[100px]">
                      {row.action === 1 &&
                        (editingRowId === row.id ? (
                          <div className="flex space-x-2">
                            <Button onClick={() => handleSave(row.id)}>Save</Button>
                            <Button onClick={handleCancel}>Cancel</Button>
                          </div>
                        ) : (
                          <EditOutlined
                            onClick={() => handleEdit(row)}
                            style={{ marginRight: 8, cursor: 'pointer' }}
                          />
                        ))}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10">No records found</div>
        )}
      </div>


    </div>
  </>
  );
};

export default ForecastingVsBudget;

